import { Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { BasicButton } from '@tassat/button';
import {
    DeclineReasonCodeEnum,
    OrderSide,
    TransactionTypeChar,
    TransactionTypeEnum
} from '@tassat/common-definitions';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { Asset, Transaction } from '@tassat/fred-definitions';
import { SecurityCurrencyAssetPair, TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import { ButtonType, WalletAddress } from '@tassat/text';
import { convertToNumber, formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';
import React from 'react';

import { ValidationData } from 'Reducers/validation';
import { ModalAction } from 'SharedComponents/modals/transactionDetailsModal';

import { muiStyles } from './transactionStyles';
import { ISendFormValues } from '../../../containers/client/overlays/send/sendForm/types';
import { IWithdrawalFormValues } from '../../../containers/client/overlays/withdrawal/withdrawalForm/types';

interface TransactionSuccessProps extends WithStyles<typeof muiStyles> {
    actionType?: ModalAction;
    assetPair: SecurityCurrencyAssetPair;
    allAssets: Asset[];
    onClose: () => void;
    transactionValues:
        ISendFormValues | ITransactFormValues | IWithdrawalFormValues |
        TransactionHistoryRecord;
    validation: ValidationData;
}

export function TransactionSuccessComponent(props: TransactionSuccessProps) {
    const {
        classes,
        actionType,
        transactionValues,
        validation,
        onClose,
        assetPair,
        allAssets
    } = props;

    const fromApproval = React.useMemo(() => {
        return (transactionValues as Transaction).RequestId;
    }, [transactionValues]);

    const isDeclined = React.useMemo(() => {
        return Object.values(DeclineReasonCodeEnum)
            .includes((transactionValues as Transaction).DeclineReason) &&
            (transactionValues as Transaction).DeclineReason !== DeclineReasonCodeEnum.SUCCESS &&
            (transactionValues as Transaction).DeclineReason !== DeclineReasonCodeEnum.NONE;
    }, [transactionValues]);

    const transactionType = React.useMemo(() => {
        if (fromApproval) {
            return (transactionValues as TransactionHistoryRecord).type === TransactionTypeChar.order ?
                (transactionValues as TransactionHistoryRecord).OrderSide
                : (transactionValues as TransactionHistoryRecord).type;
        } else {
            return (transactionValues as ITransactFormValues).Type;
        }
    }, [ fromApproval, transactionValues ]);

    const selectedAsset = React.useMemo<Asset | undefined>(() => {
        let assetId: string = (transactionValues as Transaction).AssetId;
        if (!fromApproval) {
            if (
                transactionType === TransactionTypeEnum.send ||
                transactionType === TransactionTypeChar.send ||
                transactionType === TransactionTypeChar.transfer
            ) {
                assetId = ((transactionValues as ISendFormValues).AssetType?.refData?.security?.AssetId ||
                    (transactionValues as ISendFormValues).AssetType?.refData?.currency.AssetId);
            } else {
                assetId = (transactionValues as ITransactFormValues).AssetType?.value as string;
            }
        }

        return allAssets?.find(a => a.AssetId === assetId);
    }, [ allAssets, transactionValues, fromApproval, transactionType ]);

    const transationText = React.useMemo(() => {
        if (actionType === 'decline') {
            return <span style={{ color: '#CE4A4A' }}>declined</span>;
        } else if (actionType === 'cancel') {
            return <span style={{ color: '#757575' }}>canceled</span>;
        } else if (actionType === 'approve') {
            return 'approved';
        }

        return 'submitted';
    }, [actionType]);

    const transactionRejectedText = React.useMemo(() => {
        return (transactionValues as Transaction).DeclineReason && !Object.values(DeclineReasonCodeEnum)
            .includes((transactionValues as Transaction).DeclineReason) ? <span>but your transaction was <span style={{ color: '#CE4A4A' }}>REJECTED</span></span> : '';
    }, [transactionValues]);

    if (!transactionValues && !(transactionValues as ITransactFormValues).Type) return <div />;

    const renderSuccessText = () => {
        const { transactionValues } = props;

        switch (transactionType) {
            case TransactionTypeEnum.send:
            case TransactionTypeChar.send:
            case TransactionTypeChar.transfer:

                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            You have successfully {transationText} a transfer of
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h4'
                        >
                            {selectedAsset?.Type === assetPair.security?.Type ?
                                fromApproval ?
                                    `${formatAsAssetNoSign(convertToNumber((transactionValues as Transaction).AssetQuantity), selectedAsset, true)} ${selectedAsset?.Units} of ${selectedAsset?.Ticker}`
                                    : `${formatAsAssetNoSign(convertToNumber((transactionValues as ISendFormValues).AssetAmount), selectedAsset, true)} ${selectedAsset?.Units} of ${selectedAsset?.Ticker}`
                                : fromApproval ?
                                    formatAsAsset((transactionValues as Transaction).AssetQuantity, assetPair.currency, 'symbol')
                                    : formatAsAsset((transactionValues as ISendFormValues).FiatAmount, assetPair.currency, 'symbol')}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.largePaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        {!fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {(transactionValues as ISendFormValues).Recipient ?
                                (transactionValues as ISendFormValues).Recipient.value.NickName ?
                                    `${(transactionValues as ISendFormValues).Recipient.value.NickName}`
                                    : `${(transactionValues as ISendFormValues).Recipient.value.AccountName}`
                                : (
                                    <WalletAddress
                                        buttonType={ButtonType.SMALL}
                                        isAdmin={false}
                                        walletAddress={(transactionValues as ISendFormValues).RecipientAddress}
                                    />
                                )}
                            </Typography>}
                        {fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {(transactionValues as Transaction).RecipientAccountName}
                            </Typography>}
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {
                            !isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                Transfers are processed in real-time.
                            </Typography>
                        }
                    </>
                );

            case TransactionTypeEnum.withdrawal:
            case TransactionTypeChar.withdrawal:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            You have successfully {transationText} a withdrawal for
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h4'
                        >
                            {fromApproval ?
                                formatAsAsset((transactionValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                                : formatAsAsset((transactionValues as IWithdrawalFormValues).WithdrawalAmount, assetPair.currency, 'symbol')}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.largePaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        {!fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {(transactionValues as IWithdrawalFormValues).WithdrawalInstructions.label}
                            </Typography>}
                        {fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {(transactionValues as Transaction).RecipientAccountName}
                            </Typography>}
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {
                            !isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                {'Please refer to the funds availability disclosure in our '}
                                <a
                                    href='https://tzero.com/disclosure-library/'
                                    rel='noreferrer'
                                    target='_blank'
                                >
                                    tZERO Securities customer agreement
                                </a>
                                {' for processing times.'}
                            </Typography>
                        }
                    </>
                );
            case OrderSide.SUBSCRIPTION:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            You have successfully {transationText} a subscription to
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            {`${selectedAsset?.Description} (${selectedAsset?.Ticker})`}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            for
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h5'
                        >
                            {fromApproval ?
                                formatAsAsset((transactionValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                                : (transactionValues as ITransactFormValues).AmountWithFee}
                        </Typography>
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {!isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                You will receive a confirmation when this transaction is completed.
                            </Typography>}
                    </>
                );
            case OrderSide.REDEEM:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            You have successfully {transationText} a redemption to
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            {`${selectedAsset?.Description} (${selectedAsset?.Ticker})`}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            for
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h5'
                        >
                            {fromApproval ?
                                `${formatAsAssetNoSign(convertToNumber((transactionValues as Transaction).AssetQuantity), selectedAsset, true)} ${selectedAsset?.Units}`
                                : `${formatAsAssetNoSign(convertToNumber((transactionValues as ITransactFormValues).Amount ?? '0'), selectedAsset, true)} ${selectedAsset?.Units}`}
                        </Typography>
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {!isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                You will receive a confirmation when this transaction is completed.
                            </Typography>}
                    </>
                );
            case OrderSide.BUY:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            You have successfully {transationText} a buy for
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h5'
                        >
                            {fromApproval ?
                                formatAsAsset((transactionValues as Transaction).FiatAmount, assetPair.currency, 'symbol')
                                : (transactionValues as ITransactFormValues).AmountWithFee}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            The Broker
                        </Typography>
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {!isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                You have successfully submitted your buy order.
                                You will receive a trade confirmation once your order is executed.
                            </Typography>}
                    </>
                );
            case OrderSide.SELL:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            You have successfully {transationText} a sell for
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h5'
                        >
                            {fromApproval ?
                                formatAsAsset((transactionValues as Transaction).AssetQuantity, selectedAsset, 'ticker', undefined, true)
                                : formatAsAsset((transactionValues as ITransactFormValues).Amount, selectedAsset, 'ticker', undefined, true)}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='body1'
                        >
                            The Broker
                        </Typography>
                        {transactionRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {transactionRejectedText}
                            </Typography>}
                        {!isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                You have successfully submitted your sell order.
                                You will receive a trade confirmation once your order is executed.
                            </Typography>}
                    </>
                );
            default:
                return (
                    <Typography
                        align='center'
                        className={classes.paddedText}
                        variant='body1'
                    >
                        {'You\'re transaction was successful'}.
                    </Typography>
                );
        }
    };

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='column'
            spacing={0}
        >
            {renderSuccessText()}
            <Typography
                align='center'
                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                variant='body1'
            >
                View complete details in your Transaction History.
            </Typography>
            {validation.requestId &&
                <Typography
                    align='center'
                    className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                    variant='body1'
                >
                    Request ID: {validation.requestId}
                </Typography>}
            <Grid
                className={classes.buttonContainer}
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
            >
                <BasicButton
                    onClick={onClose}
                    text='DONE'
                    type='main'
                />
            </Grid>
        </Grid>
    );
}

const TransactionSuccess = withStyles(muiStyles)(TransactionSuccessComponent);
export { TransactionSuccess };